import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Card from '@mui/material/Card';

const DEMO_RESTRICTED_ROUTES = [
  '/fx-dashboard/backtesting-dashboard',
  '/fx-dashboard/journal-entry',
  '/fx-dashboard/trade-diary',
];

const DemoRestrictionMessage = () => (
  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
    <Card sx={{ maxWidth: '600px', p: 3, textAlign: 'center' }}>
      <MDTypography variant="h4" mb={2}>
        Premium Feature
      </MDTypography>
      <MDTypography variant="body1" color="text" mb={2}>
        This feature is only available for premium members.
      </MDTypography>
      <MDTypography variant="body2" color="text">
        Please upgrade your account to access this feature.
      </MDTypography>
    </Card>
  </MDBox>
);

const ProtectedRoute = ({ element: Component, allowedUserIds, allowedRoles, userID, profileInfo, ...rest }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const isLoggedIn = localStorage.getItem('userID');
  const isAuthorized = allowedUserIds ? allowedUserIds.includes(userID) : true;
  const isRoleAuthorized = allowedRoles
    ? allowedRoles.includes(profileInfo?.role || profileInfo?.teamRole)
    : true;

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!isLoggedIn) {
        setIsLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'Users', isLoggedIn));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsDemo(userData?.isDemo || false);
          setIsAffiliate(userData?.affiliate || false);
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      }

      setIsLoading(false);
    };

    checkUserStatus();
  }, [isLoggedIn]);

  if (isLoading) {
    return null;
  }

  if (!isLoggedIn || !isAuthorized || !isRoleAuthorized) {
    return <Navigate to="/authentication/sign-in" />;
  }

  const currentPath = window.location.pathname;

  if (currentPath === '/fx-dashboard/affiliate' && !isAffiliate) {
    return null;
  }

  if (isDemo && DEMO_RESTRICTED_ROUTES.includes(currentPath)) {
    return <DemoRestrictionMessage />;
  }

  return <Component {...rest} userID={userID} profileInfo={profileInfo} />;
};

ProtectedRoute.propTypes = {
  element: PropTypes.elementType.isRequired,
  allowedUserIds: PropTypes.array,
  allowedRoles: PropTypes.array,
  userID: PropTypes.string,
  profileInfo: PropTypes.object,
};

export default ProtectedRoute;